import { useId } from "react";

import type { TtlOption } from "../lib/ttl";

/** Limits mirrored from the server's request validation. */
const MIN_URL_LENGTH = 5;
const MAX_USAGE_LENGTH = 50;

interface Props {
  url: string;
  usage: string;
  ttl: string;
  size: string;
  ttlOptions: TtlOption[];
  sizes: number[];
  defaultSize: number | null;
  pending: boolean;
  onUrlChange: (value: string) => void;
  onUsageChange: (value: string) => void;
  onTtlChange: (value: string) => void;
  onSizeChange: (value: string) => void;
  onSubmit: () => void;
}

export function GenerateForm({
  url,
  usage,
  ttl,
  size,
  ttlOptions,
  sizes,
  defaultSize,
  pending,
  onUrlChange,
  onUsageChange,
  onTtlChange,
  onSizeChange,
  onSubmit,
}: Props) {
  const urlId = useId();
  const usageId = useId();
  const ttlId = useId();
  const sizeId = useId();

  return (
    <form
      className="card form"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <div className="field">
        <label htmlFor={urlId}>Link or text</label>
        <input
          id={urlId}
          value={url}
          onChange={(event) => onUrlChange(event.target.value)}
          placeholder="https://example.com"
          minLength={MIN_URL_LENGTH}
          required
          autoComplete="url"
          spellCheck={false}
        />
        <p className="hint">What the code will contain. At least {MIN_URL_LENGTH} characters.</p>
      </div>

      <div className="field">
        <label htmlFor={usageId}>Label</label>
        <input
          id={usageId}
          value={usage}
          onChange={(event) => onUsageChange(event.target.value)}
          placeholder="spring-campaign"
          maxLength={MAX_USAGE_LENGTH}
          required
        />
        <p className="hint">Names the stored image, so you can recognise it later.</p>
      </div>

      <div className="field">
        <label htmlFor={ttlId}>Link expires</label>
        <select id={ttlId} value={ttl} onChange={(event) => onTtlChange(event.target.value)}>
          {ttlOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <p className="hint">How long the image link keeps working. The code itself never changes.</p>
      </div>

      <div className="field">
        <label htmlFor={sizeId}>Image size</label>
        <select id={sizeId} value={size} onChange={(event) => onSizeChange(event.target.value)}>
          <option value="">
            {defaultSize === null ? "Default" : `Default (${defaultSize} px)`}
          </option>
          {sizes.map((option) => (
            <option key={option} value={String(option)}>
              {option} × {option} px
            </option>
          ))}
        </select>
        <p className="hint">Width and height of the generated PNG.</p>
      </div>

      <button type="submit" className="primary" disabled={pending}>
        {pending ? "Generating…" : "Generate QR code"}
      </button>
    </form>
  );
}
